import React, {useCallback, useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import legalConsentStore, {LegalConsentStore, PendingLegalDocument} from '../../store/LegalConsentStore';
import {LEGAL_DOCUMENTS, legalPath} from '../../legal';
import useOverlayClose from '../../hooks/useOverlayClose';
import Spinner from '../Spinner';
import {FileTextIcon} from '../icons';

interface LegalReconsentDialogProps {
    /** Подменяется в тестах; в приложении — синглтон. */
    store?: LegalConsentStore;
}

const FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';

// `new Date('YYYY-MM-DD')` — UTC-полночь, в западных поясах даёт предыдущий день;
// собираем локальную дату из частей (тот же приём, что на странице документа, TC-LEG-024).
const formatDate = (iso: string): string => {
    const [year, month, day] = iso.split('-').map(Number);
    if (!year || !month || !day) {
        return iso;
    }
    return new Date(year, month - 1, day)
        .toLocaleDateString('ru-RU', {day: 'numeric', month: 'long', year: 'numeric'});
};

/** Ссылка на текст: по slug из реестра фронта, иначе — путь, который прислал сервер. */
const documentHref = (doc: PendingLegalDocument): string => {
    const local = LEGAL_DOCUMENTS.find((d) => d.type === doc.type);
    return local ? legalPath(local) : doc.path;
};

/**
 * «Обновлены правовые документы» — экран повторного согласия при старте сессии
 * (`docs/features/2026-08-26_legal-documents`, §9). Показывается, когда backend
 * прислал `legalPending` во входе/refresh; чувствительные действия не блокирует
 * (решение юриста не принято) — только просит согласие.
 *
 * Чекбокс один на все документы, как при регистрации (C-1/C-3); каждый документ —
 * ссылка в новой вкладке, чтобы диалог не терялся (C-2). «Позже», Escape и клик по
 * подложке откладывают до следующего входа.
 */
const LegalReconsentDialog: React.FC<LegalReconsentDialogProps> = observer(({store = legalConsentStore}) => {
    const [checked, setChecked] = useState(false);
    const dialogRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);
    const {shouldPrompt} = store;

    const postpone = useCallback(() => {
        if (!store.submitting) {
            store.postpone();
        }
    }, [store]);

    const overlayProps = useOverlayClose(postpone, !store.submitting);

    useEffect(() => {
        if (!shouldPrompt) {
            return undefined;
        }
        setChecked(false);
        store.loadPending();
        previouslyFocused.current = document.activeElement as HTMLElement | null;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                postpone();
                return;
            }
            if (e.key !== 'Tab' || !dialogRef.current) {
                return;
            }
            // Фокус не должен уходить за пределы модалки (доступность, styleguide).
            const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
                .filter((el) => el.offsetParent !== null || el === document.activeElement);
            if (focusable.length === 0) {
                e.preventDefault();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement as HTMLElement | null;
            const outside = !dialogRef.current.contains(active);
            if (e.shiftKey && (active === first || outside)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && (active === last || outside)) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            previouslyFocused.current?.focus?.();
        };
    }, [shouldPrompt, store, postpone]);

    if (!shouldPrompt) {
        return null;
    }

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!checked || store.submitting) {
            return;
        }
        await store.accept();
    };

    const canSubmit = checked && !store.submitting && !store.loading && store.documents.length > 0;

    return (
        <div
            className="fw-overlay fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            data-testid="legal-reconsent-overlay"
            {...overlayProps}
        >
            <div
                ref={dialogRef}
                className="relative z-10 w-full max-w-md card p-6"
                role="dialog"
                aria-modal="true"
                aria-labelledby="legalReconsentTitle"
                aria-describedby="legalReconsentDescription"
                data-testid="legal-reconsent-dialog"
            >
                <div className="w-12 h-12 rounded-2xl bg-ink-900 text-white dark:bg-white dark:text-ink-900 flex items-center justify-center mb-4">
                    <FileTextIcon className="w-6 h-6"/>
                </div>
                <h2 id="legalReconsentTitle" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Обновлены правовые документы
                </h2>
                <p id="legalReconsentDescription" className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Чтобы продолжать пользоваться сервисом на актуальных условиях, ознакомьтесь
                    с документами и подтвердите согласие.
                </p>

                <form onSubmit={submit} className="mt-5 space-y-4">
                    {store.loading && store.documents.length === 0 ? (
                        <div className="py-4 flex justify-center" data-testid="legal-reconsent-loading">
                            <Spinner/>
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100 dark:divide-white/5" data-testid="legal-reconsent-list">
                            {store.documents.map((doc) => (
                                <li key={doc.type} className="py-2.5">
                                    {/* Новая вкладка намеренно: диалог не должен теряться,
                                        когда человек уходит читать документ (C-2). */}
                                    <a
                                        href={documentHref(doc)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-brand-600 dark:text-brand-400 hover:underline"
                                    >
                                        {doc.title}
                                    </a>
                                    <p className="text-xs text-ink-500 dark:text-ink-400">
                                        Версия {doc.version} · действует с {formatDate(doc.effectiveOn)}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Тач-зона: min-h-11 и вертикальные отступы делают весь ряд
                        (чекбокс + подпись) кликабельным на ≥ 44px. */}
                    <div className="flex items-start gap-3 min-h-11 py-1">
                        <input
                            id="legalReconsent"
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                            disabled={store.submitting}
                            className="mt-0.5 h-4 w-4 rounded border-ink-300 dark:border-white/20 text-brand-600 focus:ring-brand-500"
                        />
                        <label
                            htmlFor="legalReconsent"
                            className="block text-sm leading-5 text-ink-600 dark:text-ink-300"
                        >
                            Я принимаю перечисленные документы.
                        </label>
                    </div>

                    {store.error && (
                        <p className="text-sm text-red-600 dark:text-red-400" role="alert">{store.error}</p>
                    )}

                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                        <button
                            type="button"
                            onClick={postpone}
                            disabled={store.submitting}
                            className="btn-secondary min-h-[44px]"
                        >
                            Позже
                        </button>
                        <button
                            type="submit"
                            disabled={!canSubmit}
                            className="btn-primary min-h-[44px]"
                        >
                            {store.submitting ? 'Сохранение...' : 'Принять'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default LegalReconsentDialog;
